import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { freeze } from 'immer';
import { catchError, map, Observable, of, retry, shareReplay, tap, throwError } from 'rxjs';

import { EnvError } from '../business/env-error';

@Injectable({
    providedIn: 'root',
})
export class EnvironmentService {
    public static readonly ENV_FILENAME = 'env.json';
    public static readonly DELAY_BEFORE_RETRY = 1000;
    public static readonly MAX_RETRY = 2;
    private static readonly CLASS_NAME = 'EnvironmentService';
    private lastError: EnvError;
    private cachedEnv: any = {};
    private pendingObs: Observable<any>;

    public constructor(private httpClient: HttpClient) {}

    /**
     * Return full freezed environment object
     */
    public instant(): any;
    /**
     * Return Environment value instantly if found, throws ReferenceError otherwise
     * @param {string | string[]} key - Path of node keys to the value (`PATH.TO.KEY` or ['PATH', 'TO, 'KEY'])
     * @throws ReferenceError
     */
    public instant(key: string | string[]): any;
    /**
     * Return Environment value instantly if found or defaultValue otherwise
     * This signature can only be used for FEATURES_TOGGLE keys as other keys are mandatory
     * @param {`FEATURES_TOGGLE.${string}` | ['FEATURES_TOGGLE', ...string[]]} key - Path of node keys to the value (`PATH.TO.KEY` or ['PATH', 'TO, 'KEY'])
     * @param {any} defaultValue - value returned by the method if env key is missing (instead of throwing)
     */
    public instant(key: `FEATURES_TOGGLE.${string}` | ['FEATURES_TOGGLE', ...string[]], defaultValue: any): any;
    public instant(key?: string | string[], defaultValue?: any): any {
        if (key) {
            const keys = Array.isArray(key) ? key : key.split('.');
            try {
                return keys.reduce((obj, key) => obj[key], this.cachedEnv);
            } catch (_) {
                // checking arguments length as defaultValue could be anything, including null or undefined
                if (arguments.length === 2) {
                    return defaultValue;
                }
                throw new ReferenceError(`Key ${keys.join('.')} missing in env.json`);
            }
        } else {
            return this.cachedEnv;
        }
    }

    /**
     * @deprecated use instant() instead
     */
    public getEnvironment(): Observable<any> {
        if (Object.keys(this.cachedEnv).length === 0) {
            // this should never happen in app but could be useful in unit test for those mocking env.json request
            return (this.pendingObs ??= this.loadEnvFromServer().pipe(
                // transform empty env to error, to be retro-compatible with previous getEnvironment
                map((env) => (Object.keys(env).length ? env : throwError(() => this.lastError))),
                shareReplay(),
            ));
        }
        return of(this.cachedEnv);
    }

    /**
     * This method must only be called from APP_INITIALIZER
     */
    public loadEnvFromServer() {
        return this.httpClient.get<any>(EnvironmentService.ENV_FILENAME).pipe(
            tap((env) => {
                this.setEnv(env);
            }),
            retry({ count: EnvironmentService.MAX_RETRY, delay: EnvironmentService.DELAY_BEFORE_RETRY }),
            catchError((error) => {
                this.lastError = new EnvError(`Did not succeed getting env.json : ${error.status} ${error.statusText}`);
                // if the APP_INITIALIZER return an error, the app never bootstrap. We don't want that
                return of({});
            }),
        );
    }

    /**
     * Set cached data for env.json, this method should be used in unit tests
     * @param {any} env -- env object to store
     */
    public setEnv(env: any) {
        this.cachedEnv = freeze(env, true);
    }

    /**
     * Access to the last error occured during loading of env.json
     * Used by AppComponent to launch this Error so it can be logged
     */
    public getLastError() {
        return this.lastError;
    }
}
